import {
  type Gradient,
  isGradient,
  linearGradientLine,
  lineGeometry,
  maskPath,
  type Paint,
  type ShapeElement,
  type Stroke,
} from "@cva/shared";
import type { ReactNode } from "react";
import { PatternLayer } from "./pattern";

/** SVG gradient definition that matches the CSS gradient geometry. */
export function GradientDef({
  id,
  gradient,
  width,
  height,
}: {
  id: string;
  gradient: Gradient;
  width: number;
  height: number;
}) {
  const stops = [...gradient.stops]
    .sort((a, b) => a.offset - b.offset)
    // biome-ignore lint/suspicious/noArrayIndexKey: gradient stops are positional
    .map((stop, index) => <stop key={index} offset={stop.offset} stopColor={stop.color} />);
  if (gradient.type === "radial") {
    return (
      <radialGradient id={id} cx="0.5" cy="0.5" r="0.70710678" gradientUnits="objectBoundingBox">
        {stops}
      </radialGradient>
    );
  }
  const line = linearGradientLine(gradient.angle, width, height);
  return (
    <linearGradient id={id} gradientUnits="userSpaceOnUse" {...line}>
      {stops}
    </linearGradient>
  );
}

export function dashArray(stroke: Stroke): string | undefined {
  if (stroke.dash === "dashed") return `${stroke.width * 3} ${stroke.width * 2}`;
  if (stroke.dash === "dotted") return `0 ${stroke.width * 2}`;
  return undefined;
}

function regularPolygon(cx: number, cy: number, rx: number, ry: number, sides: number): string {
  const points: string[] = [];
  for (let i = 0; i < sides; i++) {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / sides;
    points.push(`${cx + rx * Math.cos(angle)},${cy + ry * Math.sin(angle)}`);
  }
  return points.join(" ");
}

function starPoints(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  points: number,
  innerRatio: number,
): string {
  const out: string[] = [];
  for (let i = 0; i < points * 2; i++) {
    const outer = i % 2 === 0;
    const angle = -Math.PI / 2 + (i * Math.PI) / points;
    const factor = outer ? 1 : innerRatio;
    out.push(`${cx + rx * factor * Math.cos(angle)},${cy + ry * factor * Math.sin(angle)}`);
  }
  return out.join(" ");
}

/**
 * Line with end caps (arrowheads etc.), round ends or a bend. The caps are
 * sized by the stroke width and stay solid when the line is dashed.
 */
function LineWithCaps({ element, stroke }: { element: ShapeElement; stroke: Stroke }) {
  const geometry = lineGeometry({
    width: element.width,
    height: element.height,
    strokeWidth: stroke.width,
    start: element.lineStart,
    end: element.lineEnd,
    curve: element.curve,
  });
  const round = element.lineRound || stroke.dash === "dotted";
  return (
    <g>
      <path
        d={geometry.d}
        fill="none"
        stroke={stroke.color}
        strokeWidth={stroke.width}
        strokeDasharray={dashArray(stroke)}
        strokeLinecap={round ? "round" : "butt"}
        strokeLinejoin="round"
      />
      {geometry.caps.map((cap, index) => (
        <path
          // biome-ignore lint/suspicious/noArrayIndexKey: caps are positional (start, end)
          key={index}
          d={cap.d}
          fill={cap.filled ? stroke.color : "none"}
          stroke={cap.filled ? "none" : stroke.color}
          strokeWidth={cap.filled ? undefined : stroke.width}
          strokeLinecap={element.lineRound ? "round" : "butt"}
          strokeLinejoin={element.lineRound ? "round" : "miter"}
        />
      ))}
    </g>
  );
}

/** Renders a shape element as inline SVG filling its box. */
export function ShapeSvg({ element, idPrefix }: { element: ShapeElement; idPrefix: string }) {
  const { width: w, height: h } = element;
  const stroke = element.stroke && element.stroke.width > 0 ? element.stroke : undefined;
  const gradientId = `${idPrefix}-fill`;
  const fill: Paint = element.fill;
  const fillValue = isGradient(fill) ? `url(#${gradientId})` : fill;
  const strokeProps = stroke
    ? {
        stroke: stroke.color,
        strokeWidth: stroke.width,
        strokeDasharray: dashArray(stroke),
        strokeLinecap: stroke.dash === "dotted" ? ("round" as const) : ("butt" as const),
        strokeLinejoin: "miter" as const,
      }
    : {};
  // Closed shapes keep their stroke inside the box.
  const inset = stroke ? stroke.width / 2 : 0;
  const iw = Math.max(0, w - inset * 2);
  const ih = Math.max(0, h - inset * 2);
  let body: ReactNode;
  switch (element.shape) {
    case "rect":
      body = (
        <rect
          x={inset}
          y={inset}
          width={iw}
          height={ih}
          rx={Math.max(0, Math.min(element.radius - inset, iw / 2, ih / 2))}
          fill={fillValue}
          {...strokeProps}
        />
      );
      break;
    case "ellipse":
      body = (
        <ellipse cx={w / 2} cy={h / 2} rx={iw / 2} ry={ih / 2} fill={fillValue} {...strokeProps} />
      );
      break;
    case "triangle":
      body = (
        <polygon
          points={`${w / 2},${inset} ${w - inset},${h - inset} ${inset},${h - inset}`}
          fill={fillValue}
          {...strokeProps}
        />
      );
      break;
    case "polygon":
      body = (
        <polygon
          points={regularPolygon(w / 2, h / 2, iw / 2, ih / 2, element.sides)}
          fill={fillValue}
          {...strokeProps}
        />
      );
      break;
    case "star":
      body = (
        <polygon
          points={starPoints(w / 2, h / 2, iw / 2, ih / 2, element.sides, element.innerRatio)}
          fill={fillValue}
          {...strokeProps}
        />
      );
      break;
    case "line": {
      const lineStroke = stroke ?? {
        color: isGradient(fill) ? (fill.stops[0]?.color ?? "#000000") : fill,
        width: h,
        dash: "solid" as const,
      };
      if (element.lineStart || element.lineEnd || element.lineRound || element.curve) {
        body = <LineWithCaps element={element} stroke={lineStroke} />;
        break;
      }
      body = (
        <line
          x1={0}
          y1={h / 2}
          x2={w}
          y2={h / 2}
          stroke={lineStroke.color}
          strokeWidth={lineStroke.width}
          strokeDasharray={dashArray(lineStroke)}
          strokeLinecap={lineStroke.dash === "dotted" ? "round" : "butt"}
        />
      );
      break;
    }
  }
  if (element.mask && element.shape !== "line") {
    // Frames: the outline of an image mask, stroke kept inside like the shapes.
    body = (
      <path
        d={maskPath(element.mask, iw, ih)}
        transform={inset ? `translate(${inset} ${inset})` : undefined}
        fill={fillValue}
        {...strokeProps}
      />
    );
  }
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ position: "absolute", inset: 0, overflow: "visible", display: "block" }}
      aria-hidden="true"
    >
      {isGradient(fill) ? (
        <defs>
          <GradientDef id={gradientId} gradient={fill} width={w} height={h} />
        </defs>
      ) : null}
      {body}
      {element.pattern ? (
        <PatternLayer element={element} id={`${idPrefix}-pattern`} inset={inset} />
      ) : null}
    </svg>
  );
}
